import { UserTradingAnalyticsPeriod } from '@root/features/user/user-trading-analytics/models/user-trading-analytics-period'

/**
 * What the period selector asks the server for.
 *
 * The server takes a window in hours or in days, and hours win when both are
 * sent — a caller asking for six hours has said something more specific than
 * the default it came with.
 *
 * **What the window reaches, and what it does not.** The day-by-day series
 * behind the PnL chart and calendar is cut to it. The positions and the
 * totals above them are not: they are read from `wallet_token_stats`, which
 * aggregates per wallet and token with no time dimension, so there is nothing
 * in that table to cut. Making those follow the period means computing them
 * from the trades themselves, which is a different query and a different
 * cost — see the degen-hours read for the shape of it.
 */
export type ProfileWindow = { hours: number } | { days: number }

const WINDOWS: Record<UserTradingAnalyticsPeriod, ProfileWindow> = {
  [UserTradingAnalyticsPeriod['1H']]: { hours: 1 },
  [UserTradingAnalyticsPeriod['6H']]: { hours: 6 },
  [UserTradingAnalyticsPeriod['12H']]: { hours: 12 },
  [UserTradingAnalyticsPeriod['1D']]: { days: 1 },
  [UserTradingAnalyticsPeriod['7D']]: { days: 7 },
  // A year, because the server's window is bounded and the wallet stats it
  // reads keep everything anyway: asking for more would not return more.
  [UserTradingAnalyticsPeriod.ALL]: { days: 365 },
}

export const windowOf = (period: UserTradingAnalyticsPeriod): ProfileWindow =>
  WINDOWS[period]
